import COUNTRIES from "../components/autocompletes/countries/countries";
import {ColumnIds, IncludeExclude} from "../utils/types";
import {PageState,IdName,CategoricalCellDto} from "../utils/types";
import {TRAFFIC_TYPES_ARRAY} from "../utils/misc";
import StateHelper from "../utils/StateHelper";
import dayjs from "../utils/dayjs";
import { calculateISO8601WeekBoundaries } from "../utils/DateUtil";
import { DatagridHelper } from "./DatagridHelper";

// Helper function to add traffic source (publisher/site) to state
function addToTrafficSourceIds(pageState: PageState, publisherId: string, siteId: string) {
  if (!pageState.publisherIds) pageState.publisherIds = [];
  if (!pageState.siteIds) pageState.siteIds = [];

  // If we have a specific siteId, only add the siteId (not the publisherId)
  // to avoid Cartesian product
  if (siteId && siteId !== "***") {
    pageState.siteIds.push(siteId);
  } else if (publisherId && publisherId !== "***") {
    // Only add publisherId if we don't have a specific site
    if (!pageState.publisherIds.includes(publisherId)) {
      pageState.publisherIds.push(publisherId);
    }
  }
}

function addTrafficSourceToState(pageState:PageState, categoricalCellDto:CategoricalCellDto, tmpValue:any, allowStateModification: boolean = true): PageState {
  let publisherId: string = null;
  let siteId: string = null;

  if (categoricalCellDto.columnId === ColumnIds.apptapPublisherName) {
    publisherId = categoricalCellDto.publisherId;
    siteId = categoricalCellDto.siteId;
  } else {
    const parts = tmpValue.split(":");
    publisherId = parts[0] || null;
    siteId = parts.length > 1 ? parts[1] : null;
  }

  if (!allowStateModification) {
    // URL generation only: return copy without side effects
    const newPageState = {...pageState};
    newPageState.publisherIds = publisherId && publisherId !== "***" ? [publisherId] : [];
    newPageState.siteIds = siteId && siteId !== "***" ? [siteId] : [];
    newPageState.trafficSourcesIncludeExclude = IncludeExclude.Include;
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
  //- Whenever the user filters with the drill-down menu, the current filter should be replaced, not added to.
  //reset state
  pageState.publisherIds = [];
  pageState.siteIds = [];

  addToTrafficSourceIds(pageState, publisherId, siteId);
  return pageState;
}

function addCountryToState(pageState:PageState, value: any, allowStateModification: boolean = true): PageState{
  if (!allowStateModification) {
    // URL generation only: return copy without side effects
    const newPageState = {...pageState};
    newPageState.countries = value;
    newPageState.countriesIncludeExclude = IncludeExclude.Include;
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
  pageState.countries = null;

  const elements : any[] = [];
  for( let i = 0;  i < COUNTRIES.length; i++ ){
    const element : any =  COUNTRIES[i];
    if ( element.id === value ) {
      pageState.countries = pageState.countries === null ? value : pageState.countries + "," + value;
      elements.push(element);
      break;
    }
  }
  return pageState;
}

function addSiteIdToState(pageState:PageState, categoricalCellDto: CategoricalCellDto, tmpValue:any, allowStateModification: boolean = true): PageState {
  let siteId: string = tmpValue;

  if (!allowStateModification) {
    // URL generation only: return copy without side effects
    // Since siteIds are globally unique, only send siteId
    const newPageState = {...pageState};
    newPageState.publisherIds = [];
    newPageState.siteIds = [siteId];
    newPageState.trafficSourcesIncludeExclude = IncludeExclude.Include;
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
  //reset state
  pageState.publisherIds = [];
  pageState.siteIds = [];

  // Only send siteId (no publisherId needed since siteIds are unique)
  addToTrafficSourceIds(pageState, null, siteId);
  return pageState;
}

function addTraffictypeToState(pageState:PageState, value:any, allowStateModification: boolean = true): PageState {
  if (!allowStateModification) {
    // URL generation only: return copy without side effects
    const newPageState = {...pageState};
    newPageState.trafficTypes = value;
    newPageState.trafficTypesIncludeExclude = IncludeExclude.Include;
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
  pageState.trafficTypes = null;

  const trafficTypesArray : IdName[] = [];

  const elements : any[] = [...trafficTypesArray];

  for( let i = 0;  i < TRAFFIC_TYPES_ARRAY.length; i++ ){
    const element : any =  TRAFFIC_TYPES_ARRAY[i];
    if ( element.id === value ) {
      pageState.trafficTypes = pageState.trafficTypes === null ? value : pageState.trafficTypes + "," + value;
      elements.push(element);
      break;
    }
  }
  return pageState;
}

function addProductIdToState(pageState:PageState, categoricalCellDto: CategoricalCellDto, value:any, allowStateModification: boolean = true): PageState {
  if (!allowStateModification) {
    // URL generation only: return copy without side effects
    const newPageState = {...pageState};
    newPageState.productIds = value;
    newPageState.productsIncludeExclude = IncludeExclude.Include;
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
  pageState.productIds = value;
  pageState.productsIncludeExclude = IncludeExclude.Include;
  return pageState;
}

function addCampaignIdToState(pageState:PageState, categoricalCellDto: CategoricalCellDto, value:any, allowStateModification: boolean = true): PageState {
  // If state modification is not allowed (URL generation), return a copy with minimal changes
  if (!allowStateModification) {
    const newPageState = {...pageState};
    newPageState.campaignIds = value;
    newPageState.campaignIdsIncludeExclude = IncludeExclude.Include;
    // Also set demand source if available
    const campaignSource = categoricalCellDto.campaignSource;
    if (campaignSource && campaignSource !== "***") {
      newPageState.demandSourceIds = campaignSource;
      newPageState.demandSourceIdsIncludeExclude = IncludeExclude.Include;
    }
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
  pageState.campaignIds = value;
  pageState.campaignIdsIncludeExclude = IncludeExclude.Include;

  // Also set demand source if available
  const campaignSource = categoricalCellDto.campaignSource;
  if (campaignSource && campaignSource !== "***") {
    pageState.demandSourceIds = campaignSource;
    pageState.demandSourceIdsIncludeExclude = IncludeExclude.Include;
  }

  return pageState;
}

function addCampaignSourceToState(pageState:PageState, categoricalCellDto:CategoricalCellDto, value:any, allowStateModification: boolean = true): PageState {
  // Filter by demand source (campaign source)

  // If state modification is not allowed (URL generation), return a copy with minimal changes
  if (!allowStateModification) {
    const newPageState = {...pageState};
    newPageState.demandSourceIds = value;
    newPageState.demandSourceIdsIncludeExclude = IncludeExclude.Include;
    return newPageState;
  }

  // Modify global state directly
  pageState = StateHelper.getPageState();
  pageState.demandSourceIds = value;
  pageState.demandSourceIdsIncludeExclude = IncludeExclude.Include;
  return pageState;
}

function addPublisherPlacementToState(pageState:PageState, categoricalCellDto:CategoricalCellDto, cellValue:any, allowStateModification: boolean = true): PageState {
  if (!allowStateModification) {
    // URL generation only: return copy without side effects
    const newPageState = {...pageState};
    newPageState.placementIds = [cellValue];
    newPageState.placementsIncludeExclude = IncludeExclude.Include;
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
// To solve the confusion with traffic sources / placements and how they both affect the same filter - until we get a better UX in place - how about:
// - When no "placement" filter selected: Everything acts like it currently does. No change in behavior.
// - When there is a "placement" filter selected:
//      - Disable (and ignore) the traffic sources box (but keep the selected items visible)
//      - Filter is built ONLY from the placements box.
//      - (the user must "clear" the placements box to re-enable the traffic sources box)
//      - Add a message on the traffic sources box: "Clear Publisher Placements filter to filter by Traffic Sources"
  //reset state
  //clean both states
  pageState.publisherIds = [];
  pageState.siteIds = [];
  pageState.placementIds = [];

  pageState.placementIds.push(cellValue);
  return pageState;
}

function addDateRangeToState(pageState:PageState, cellValue:any, allowStateModification: boolean = true): PageState {
  if (!allowStateModification) {
    // URL generation only: return copy without side effects
    const newPageState = {...pageState};
    const dateStart = dayjs.utc(cellValue);
    const dateEnd = dateStart;
    newPageState.dateStart = dateStart;
    newPageState.dateEnd = dateEnd;
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
  const date = dayjs.utc(cellValue);
  pageState.dateEnd = date;
  pageState.dateStart = date;
  return pageState;
}

function addMonthToState(pageState:PageState, cellValue:any, allowStateModification: boolean = true): PageState {
  if (!allowStateModification) {
    // URL generation only: return copy without side effects
    const newPageState = {...pageState};
    const dateStart = dayjs.utc(cellValue).startOf("month").startOf("day");
    const dateEnd = dayjs.utc(cellValue).endOf("month").startOf("day");
    newPageState.dateStart = dateStart;
    newPageState.dateEnd = dateEnd;
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
  pageState.dateEnd = dayjs.utc(cellValue).endOf("month").startOf("day");
  pageState.dateStart = dayjs.utc(cellValue);
  return pageState;
}

function addWeekToState(pageState:PageState, cellValue:any, allowStateModification: boolean = true): PageState {
  if (!allowStateModification) {
    // URL generation only: return copy without side effects
    const newPageState = {...pageState};
    const [dateStart, dateEnd] = calculateISO8601WeekBoundaries(cellValue);
    newPageState.dateStart = dateStart;
    newPageState.dateEnd = dateEnd;
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
  const [dateStart, dateEnd] = calculateISO8601WeekBoundaries(cellValue);
  pageState.dateEnd = dateEnd;
  pageState.dateStart = dateStart;
  return pageState;
}

function addCLinkIdOrOwnerToState(pageState:PageState, cellValue:any, allowStateModification: boolean = true): PageState {
  if (!allowStateModification) {
    // URL generation only: return copy without side effects
    const newPageState = {...pageState};
    newPageState.cLinks = [cellValue];
    newPageState.cLinksIncludeExclude = IncludeExclude.Include;
    return newPageState;
  }

  // Actual filtering: modify global state directly
  pageState = StateHelper.getPageState();
  const clinks:string[] = [];
  clinks.push(cellValue);
  pageState.cLinks = clinks;
  return pageState;
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const addFilterToPageState = function(pageState:PageState, categoricalCellDto:CategoricalCellDto, value:any, allowStateModification: boolean = true): PageState {
  const columnId : string = categoricalCellDto.columnId;

  switch( columnId ){
    case ColumnIds.date:
      pageState = addDateRangeToState(pageState, value, allowStateModification);
      break;

    case ColumnIds.month:
      pageState = addMonthToState(pageState, value, allowStateModification);
      break;

    case ColumnIds.week:
      pageState = addWeekToState(pageState, value, allowStateModification);
      break;
  
    case ColumnIds.apptapPlacementID:
      break;

    case ColumnIds.apptapPublisherName:
    case ColumnIds.apptapPublisherID:
      pageState = addTrafficSourceToState(pageState, categoricalCellDto, value, allowStateModification);
      pageState.trafficSourcesIncludeExclude = IncludeExclude.Include;
      break;

    case ColumnIds.apptapSiteID:
      pageState = addSiteIdToState(pageState, categoricalCellDto, value, allowStateModification);
      pageState.trafficSourcesIncludeExclude = IncludeExclude.Include;
      break;

    case ColumnIds.publisherPlacementID:
      pageState = addPublisherPlacementToState(pageState, categoricalCellDto, value, allowStateModification);
      pageState.placementsIncludeExclude = IncludeExclude.Include;
      break;

    case ColumnIds.country:
      pageState = addCountryToState(pageState, value, allowStateModification);
      pageState.countriesIncludeExclude = IncludeExclude.Include;
      break;

    case ColumnIds.trafficType:
      pageState = addTraffictypeToState(pageState, value, allowStateModification);
      pageState.trafficTypesIncludeExclude = IncludeExclude.Include;
      break;
          
    case ColumnIds.productID:
      pageState = addProductIdToState(pageState, categoricalCellDto, value, allowStateModification);
      pageState.productsIncludeExclude = IncludeExclude.Include;
      break;

    case ColumnIds.campaignID:
      pageState = addCampaignIdToState(pageState, categoricalCellDto, value, allowStateModification);
      pageState.campaignIdsIncludeExclude = IncludeExclude.Include;
      if (categoricalCellDto.campaignSource && categoricalCellDto.campaignSource !== "***") {
        pageState.demandSourceIdsIncludeExclude = IncludeExclude.Include;
      }
      break;

    case ColumnIds.campaignSource:
      pageState = addCampaignSourceToState(pageState, categoricalCellDto, value, allowStateModification);
      break;

    case ColumnIds.cLinkId:
    case ColumnIds.cLinkOwner:
      pageState = addCLinkIdOrOwnerToState(pageState, value, allowStateModification);
      pageState.cLinksIncludeExclude = IncludeExclude.Include;
      break;
  }
  return pageState;
};

export const addFilterAndGroupByToPageState = function (pageState:PageState, categoricalFieldsDto:CategoricalCellDto, columnIdToGroupBy:string, value:any, allowStateModification: boolean = true):PageState {
  pageState = addFilterToPageState(pageState, categoricalFieldsDto, value, allowStateModification);

  let groupByArr = pageState.groupBy ? pageState.groupBy.split(",") : [];
  if ( columnIdToGroupBy === ColumnIds.date || columnIdToGroupBy === ColumnIds.month || columnIdToGroupBy === ColumnIds.week ){
    groupByArr = DatagridHelper.setSinglePeriodGroupBy(groupByArr, columnIdToGroupBy);
  } else {
    groupByArr.unshift(columnIdToGroupBy);
  }

  pageState.groupBy = groupByArr.join(",");
  return pageState;
};
